import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { CreateCatalogRequest } from "@/lib/api/catalog";
import { JournalTagList } from "@/components/catalog/JournalFieldParts";
import { RequiredLabel } from "@/components/catalog/RequiredLabel";

type Biblio = CreateCatalogRequest["biblio"];

interface JournalContributorsFieldsProps {
  value: Biblio;
  onChange: (value: Biblio) => void;
  errors: Record<string, string>;
}

export function JournalContributorsFields({
  value,
  onChange,
  errors,
}: JournalContributorsFieldsProps) {
  const [editor, setEditor] = useState("");
  const [subject, setSubject] = useState("");
  const subjectLimitReached = (value.subjects?.length ?? 0) >= 3;
  const addEditor = () => {
    const next = editor.trim();
    if (!next) return;
    onChange({ ...value, editors: [...(value.editors ?? []), next] });
    setEditor("");
  };
  const addSubject = () => {
    const next = subject.trim();
    if (!next || subjectLimitReached) return;
    onChange({ ...value, subjects: [...(value.subjects ?? []), next] });
    setSubject("");
  };
  return (
    <>
      <div>
        <RequiredLabel htmlFor="journal-editor" required>
          Editor
        </RequiredLabel>
        <div className="mt-1.5 flex gap-2">
          <Input
            id="journal-editor"
            value={editor}
            onChange={(event) => setEditor(event.target.value)}
            placeholder="Enter editor name and press Enter"
            aria-invalid={Boolean(errors.editors)}
            aria-describedby={errors.editors ? "journal-editor-error" : undefined}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                addEditor();
              }
            }}
          />
          <Button type="button" variant="outline" onClick={addEditor}>
            Add editor
          </Button>
        </div>
        {errors.editors ? (
          <p id="journal-editor-error" className="mt-1 text-sm text-red-600">
            {errors.editors}
          </p>
        ) : null}
        <JournalTagList
          values={value.editors ?? []}
          onRemove={(index) =>
            onChange({
              ...value,
              editors: value.editors?.filter((_, itemIndex) => itemIndex !== index),
            })
          }
        />
      </div>
      <div>
        <RequiredLabel htmlFor="journal-subject" required>
          Subject heading
        </RequiredLabel>
        <div className="mt-1.5 flex gap-2">
          <Input
            id="journal-subject"
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
            placeholder="Enter subject and press Enter"
            disabled={subjectLimitReached}
            aria-invalid={Boolean(errors.subjects)}
            aria-describedby={errors.subjects ? "journal-subject-error" : undefined}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                addSubject();
              }
            }}
          />
          <Button
            type="button"
            variant="outline"
            onClick={addSubject}
            disabled={subjectLimitReached}
          >
            Add subject
          </Button>
        </div>
        {subjectLimitReached ? (
          <p className="mt-1 text-xs text-muted-foreground">Maximum 3 subject headings.</p>
        ) : null}
        {errors.subjects ? (
          <p id="journal-subject-error" className="mt-1 text-sm text-red-600">
            {errors.subjects}
          </p>
        ) : null}
        <JournalTagList
          values={value.subjects ?? []}
          onRemove={(index) =>
            onChange({
              ...value,
              subjects: value.subjects?.filter((_, itemIndex) => itemIndex !== index),
            })
          }
        />
      </div>
    </>
  );
}
